import React, { useCallback, useEffect } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { Button, Grid } from '@mui/material';
import { ReactHookFormSearchableSelect } from '../../../../custom-components/form-components';
import { Add as AddIcon } from '@mui/icons-material';
import _ from 'lodash';
import ComponentMap from '../ComponentMap';

export function generateFieldArrayDefaults(arrayFields, defaultValues) {
    return Object.keys(defaultValues).map((key) => {
        return { filterType: { id: key, name: arrayFields.find((field) => field.name === key)?.label } };
    });
}

function FieldArray({ name, ArrayFields, props: { addLabel } = {} }) {
    const {
        control,
        getValues,
        setValue,
        formState: { defaultValues },
        updateSelectedValues,
        watch,
    } = useFormContext();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${name}_filters`,
    });

    const filterWatcher = watch(`${name}_filters`);
    const filterTypes = ArrayFields.map(({ name: fieldName, label: fieldLabel }) => ({ name: fieldLabel, id: fieldName }));
    const remainingFilters = filterTypes.filter(({ id }) => filterWatcher?.find(({ filterType }) => filterType?.id === id) == null);

    useEffect(() => {
        if (filterWatcher == null && defaultValues?.[name] != null) {
            setValue(`${name}_filters`, generateFieldArrayDefaults(ArrayFields, defaultValues?.[`${name}`]));
        }
    }, [defaultValues]);

    useEffect(() => {
        const values = getValues(name);
        updateSelectedValues(name, values);
    }, [watch(name)]);

    const appendField = () => {
        if (filterWatcher?.length !== ArrayFields.length) {
            append({ filterType: undefined });
        }
    };

    const removeField = (index) => {
        const id = filterWatcher[index]?.filterType?.id;
        if (id) setValue(`${name}.${id}`, undefined);
        remove(index);
    };

    return (
        <>
            {fields.map((field, index) => {
                const { name: fieldName, valueProps, label: fieldLabel, Component: fieldComponent, ...fieldRest } = ArrayFields.find((f) => filterWatcher[index].filterType?.id === f.name) ?? {};
                const Component = ComponentMap[fieldComponent];
                const fieldPath = `${name}.${fieldName}`;
                return (
                    <Grid key={field.id} container spacing={2} alignItems="center">
                        <Grid item xs={5}>
                            <ReactHookFormSearchableSelect
                                onChange={() => {
                                    setValue(fieldPath, undefined);
                                }}
                                name={`${name}_filters.${index}.filterType`}
                                options={remainingFilters}
                                variant={'outlined'}
                            />
                        </Grid>
                        {Component && (
                            <Grid item xs={5}>
                                <Component
                                    props={valueProps}
                                    {...fieldRest}
                                    key={fieldPath}
                                    name={fieldPath}
                                    fieldName={name}
                                    componentName={fieldLabel}
                                    {...{ control, index }}
                                    // wrap
                                />
                            </Grid>
                        )}
                        <Grid item xs={2}>
                            <Button onClick={() => removeField(index)}>
                                <HighlightOffIcon />
                            </Button>
                        </Grid>
                    </Grid>
                );
            })}

            <Button aria-label="add" startIcon={<AddIcon />} variant="outlined" disabled={filterWatcher?.length === ArrayFields.length} onClick={appendField}>
                {addLabel || 'Add'}
            </Button>
        </>
    );
}

export default FieldArray;
